import React from 'react';
import { 
  ALL, 
  ASSESSED, 
  COMMUNITY, 
  GOVERNANCE, 
  UNASSESSED 
} from '../../static/keyWords.js';
import { 
  ForgeIcon, 
  ForgeInlineMessage 
} from '@tylertech/forge-react';
import { SCORE_TYPE_NAME_FINAL_ID, USER_ROLE } from '../../static/constants.js';

export const STEPS = [
  {label: 'BPA', value: 0 },
  {label: 'Community', value: 1 }, 
  {label: 'Governance', value: 2 },
];

export const SCORING_PAGINATION_PAGE_SIZE_SELECTOR_VALUES = [5, 10, 20, 50, 100];
export const SCORING_DEFAULT_PAGINATION_PAGE_SIZE = 5;

export const getPredictionColumnDefs = (retrieveColumnsFromStorage, columnsOrderAndSize) =>{ 

  const columns = [
    {
      field: 'program',
      headerName: 'Program',
      cellClass: 'truncate-text',
      context: 'Program',
    },
    {
      field: 'description',
      headerName: 'Program description',
      cellClass: 'truncate-text',
      context: 'Program description',
    },
    {
      field: 'score',
      headerName: 'Score',
      context: 'Score',
    },
  ];
  return columnsOrderAndSize?.['DepartmentScoring-scoringPredictionColumns']?.length>0
    ? retrieveColumnsFromStorage(columns, 'DepartmentScoring-scoringPredictionColumns')
    : columns;
};

export const defaultPredictionColumnDefs = {
  resizable: true,
  sortable:true,
  suppressFloatingFilterButton: true,
};

export const chartOptions = (theme) => {
  return {
    indexAxis: 'y',
    xAxis : {
      display: false
    },
    yAxis : {
      display: false
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          beforeLabel: (context) => context.dataset.fullLabels[context.dataIndex],
          label: (context) => context.raw
        },
        backgroundColor: theme == 'light' ? '#3f51b5' : '#8c9eff',
        borderColor: theme == 'light' ? '#3f51b5' : '#8c9eff',
        borderWidth: 1,
      }
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false
        },
        ticks: {
          beginAtZero: true,
        },
        stacked: false,
      },
      y: {
        barThickness: 10,
        grid: {
          display: false
        },
        stacked: false,
        ticks: {
          font: {
            size: 12,
          },
          color: theme == 'light' ? '#212121' : '#e4e4e4'
        }
      }
    },
    responsive: false
  };
};

export const filterByDepartment = (data, filter) => {
  if (!filter) return data;
  return data.filter((program) => filter.value.map(val=>val.name).includes(program.organizationLevels.split('--')[0]));   
};

export const filterByDivision = (data, filter) => {
  if (!filter) return data;
  return data.filter((program) =>filter.value.map(val=>val.name).includes(program.organizationLevels.split('--')[1]));      
};

export const filterByServiceType = (data, filter) => {
  if (!filter) return data;
  return data.filter((program) => filter.value.map(val=>val.name).includes(program.serviceType.name));         
};


export const filterByBPA = (data, filter, isFinal) => {
  if (!filter) return data;
  let _data = [...data];
  filter.options.forEach(subFilter => {
    _data = filterSubBPA(_data, subFilter, filter.type, isFinal);
    return _data;
  });
  return _data;
};

export const filterByResultType = (data, filter, isFinal) => {
  if (!filter) return data;
  let _data = [...data];
  filter.options.forEach(subFilter => {
    _data = filterSubResult(_data, subFilter, filter.type, isFinal);
    return _data;
  });
  return _data;
};

const filterSubResult = (data, filter, filterType, isFinal) => {
  let _data = [...data];
  let selectedSubTags = filter.value.map(val=>val.name);
  let filteredData = _data.filter(program => {
    let result = program.resultTypes.filter(result => result.name !== filterType);
    let combinedResults = result.reduce((acc, result) => {
      return [...acc, ...result.results];
    }, []);
    if (combinedResults) {
      let tools = combinedResults.find(result => result.abbreviation === filter.name);
      if (tools) {
        let scores = [];
        if (isFinal) {
          scores = tools.scores
            .filter(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID)
            .sort((a, b) => b.lastUpdated.localeCompare(a.lastUpdated))
            .map(score=> score.scoreOption.name ? score.scoreOption.name : score.scoreOption);
        } else {
          scores = tools.scores
            .filter(score => score.scoreTypeId !== SCORE_TYPE_NAME_FINAL_ID)
            .sort((a, b) => b.lastUpdated.localeCompare(a.lastUpdated))
            .map(score=> score.scoreOption.name ? score.scoreOption.name : score.scoreOption);
        }
        
        if ((scores || []).length > 0) {
          if (selectedSubTags.includes(scores[0])) {
            return program;
          }
        }
      }
    }
  });
  return filteredData;
};


const filterSubBPA = (data, filter, filterType, isFinal) => {
  const _data = [...data];
  const selectedSubTags = filter.value.map(val=>val.name);
  const filteredData = _data.filter(program => {
    const BPAResult = program.resultTypes.find(result => result.name === filterType);
    if (BPAResult) {
      const BPADemand = BPAResult.results.find(result => result.abbreviation === filter.name);
      if (BPADemand) {
        let scores = [];
        if (isFinal) {
          scores = BPADemand.scores
            .filter(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID)
            .sort((a, b) => b.lastUpdated.localeCompare(a.lastUpdated))
            .map(score=> score.scoreOption.name ? score.scoreOption.name : score.scoreOption);
        } else {
          scores = BPADemand.scores
            .filter(score => score.scoreTypeId !== SCORE_TYPE_NAME_FINAL_ID)
            .map(score=> score.scoreOption.name ? score.scoreOption.name : score.scoreOption);
        }
        
        if ((scores || []).length > 0) {
          if (selectedSubTags.includes(scores[0])) {
            return program;
          }
        }
      }
    }
  });
  return filteredData;
};

export const filterByQuartile = (data, filter) => {
  return data.filter((program) => filter.value.map(val=>val.name).includes(program.finalQuartileVerbal));
};

export const filterByQuartileDiffs = (data, filter) => {
  return data.filter((program) => filter.value.map(val=>Number(val.name)).includes(program.quartileDiff));
};

export const filterByKeyword = (data, keyword) => {
  if (keyword.length === 0) {
    return data;
  }

  let filteredByKeyword = data.filter(program =>
    program.name.toLowerCase().includes(keyword.toLowerCase()) ||
    program.description.toLowerCase().includes(keyword.toLowerCase()) ||
    program.organizationLevels.toLowerCase().includes(keyword.toLowerCase())
  );
  return filteredByKeyword;
};

export const handleFiltering = (programs,selectedDepartments, selectedDivisions) => {
  let _filteredPrograms;
  if (selectedDepartments.length === 0 && selectedDivisions.length === 0) return programs;
  else if (selectedDepartments.length !== 0 && selectedDivisions.length === 0) {
    _filteredPrograms = filterByDepartment(programs, selectedDepartments);
  }
  else if (selectedDepartments.length === 0 && selectedDivisions.length !== 0) {
    _filteredPrograms = filterByDivision(programs, selectedDivisions);
  } else {
    let tempPrograms = filterByDepartment(programs, selectedDepartments);
    _filteredPrograms = filterByDivision(tempPrograms, selectedDivisions);
  }

  return _filteredPrograms;
};

export const getLatestScoreForProgram = (programId, newScores, currentResult, scoresByResultAndProgram) => {    
  if (newScores[currentResult.name] && newScores[currentResult.name][programId]) {
    return newScores[currentResult.name][programId];
  }
  else if (scoresByResultAndProgram[currentResult.name] && scoresByResultAndProgram[currentResult.name][programId]) {
    return scoresByResultAndProgram[currentResult.name][programId];
  }
  return {};
};

export const handleTextLength = (text, separator) => {
  if (text?.length > separator) return `${text.slice(0, separator)}...`;
  return text || '';
};

export const noResultType = (message) => {
  return (
    <ForgeInlineMessage theme="info" style={{width: 'calc(100% - 32px)'}} className="margin">
      <ForgeIcon external externalType="all" slot="icon" name="info" />
      <p className="forge-typography--body2">{message}</p>
    </ForgeInlineMessage>
  );
};

export const mapProgramsForPredictions = (programs, results) => {
  return programs.map(x=>{
    return {
      id: x.id,
      name: x.name,
      description: x.description,
      cost_center: x.costCenter.name,
      resultTypes: {
        community: x.serviceType.name === COMMUNITY ? results[COMMUNITY].map(y => { return { id: y.id, name: y.abbreviation.toLowerCase() }; }) : null,
        governance: x.serviceType.name === GOVERNANCE ? results[GOVERNANCE].map(y => { return { id: y.id, name: y.abbreviation.toLowerCase() }; }) : null,
      }
    };
  });
};

export const mapScoresForSaving = (scoresToSave, newScores, resultName, resultId) => {
  for (const programId in newScores[resultName]) {
    let newScore = {};
    newScore.resultId = resultId;
    newScore.programId = parseInt(programId);
    newScore.scoreOptionId = newScores[resultName][programId].scoreOptionId;
    newScore.id = newScores[resultName][programId].editScoreId;
    newScore.comments = newScores[resultName][programId].comments;
    newScore.value = newScores[resultName][programId].value;
    newScore.scoreTypeId = 1;
    scoresToSave.push(newScore);
  }
};

export const filterProgramsByAssessment = (selected, programs, savedScores = {}) => {
  const isAssessed = ({ id }) => Boolean(savedScores[id]);

  switch (selected) {
    case ALL:
      return programs;
    case ASSESSED:
      return programs.filter(isAssessed);
    case UNASSESSED:
      return programs.filter((program) => !isAssessed(program));
    default:
      return programs;
  }
};

export const predictionInfoChartBuilder = (predictionInfo, radioOptions, theme, setGraphData) => {
  const labelsArr = [];
  const fullLabelsArr = [];
  const valuesArr = [];

  const data = predictionInfo && predictionInfo.closeNeighbors
    .map((neighbor) => neighbor.final_score)
    .reduce((acc, curr) => {
      const key = Math.floor(curr);
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key]++;
      return acc;
    }, {});
    
  for (const key in data) {
    const text = radioOptions.find(x=>x.weight===Number(key))?.name;
    labelsArr.push(handleTextLength(text, 25));
    fullLabelsArr.push(text);
    valuesArr.push(data[key]);
  }

  setGraphData([{
    labels: labelsArr,
    datasets: [
      {
        data: valuesArr ? valuesArr : [],
        backgroundColor: theme == 'light' ? '#3f51b5' : '#8c9eff',
        borderColor: theme == 'light' ? ['#3f51b5'] : ['#8c9eff'],
        borderWidth: 1,
        barThickness: 27,
        fullLabels: fullLabelsArr
      },
    ]
  }]);
};

export const filterHeaderButtons = (button, cardSelected, user) =>
  (cardSelected || button.id !== 2) &&
  (button.id !== 1 || user?.role === USER_ROLE.ADMIN);

export const getHeaderButtons = (setOpenRecommendDialog, setOpenFilters, isDrawerOpen, onChangeDrawer, user, cardSelected) => {
  return [
    {
      name: 'Recommend',
      onClickFunction: () => setOpenRecommendDialog(true),
      id: 1,
      useAiButton: true,
      showCondition: true,
      ...(user?.isViewOnly && {['disabled']:user?.isViewOnly})
    },
    {
      variant: 'primary',
      onClickFunction: () => setOpenFilters(true),
      id: 0, 
      name: <><ForgeIcon external externalType="all"name="filter_variant"/> Filter</>,
      filterBadge: true
    },
    {
      id: 2,
      name: 'definitions',
      type: 'iconButton',
      icon: !isDrawerOpen ? 'show_panel_right' : 'hide_panel_right',
      before: true,
      tooltip: !isDrawerOpen ? 'Show definitions' : 'Close definitions',
      style: { margin: '0px 0px 0px 10px' },
      onClickFunction: onChangeDrawer,
    }
  ].filter(button => filterHeaderButtons(button, cardSelected, user));
};

export const decideWidthBasedOnConditions = (state, cardSelected, isDrawerOpen) => {
  return state.navDrawerOpen 
    ? cardSelected 
      ? '100%'
      : '80%' 
    : cardSelected 
      ? isDrawerOpen 
        ? 'calc(80% + 205px)'
        : '80%'
      : '60%';
};

export const getDynamicProgress = (programs, existingScores, newScores) => {
  const programIds = new Set(programs.map(({ id }) => String(id)));
  const existingScoreProgramIds = existingScores
    .map(({ programId }) => String(programId))
    .filter(programId => programIds.has(programId));
  const newScoreProgramIds = newScores
    ? Object.keys(newScores).filter(programId => programIds.has(programId))
    : [];
  const allProgramsWithScores = new Set([
    ...existingScoreProgramIds,
    ...newScoreProgramIds,
  ]);
  return programs.length ? (100 * allProgramsWithScores.size) / programs.length : 0;
};
